/**
 * Dashboards — folder-grouped list of Tableau dashboards with inline embedding.
 *
 * Two states:
 *   1. List view: dashboards grouped by folder (Sales, Call Center, Marketing,
 *      Owner), each rendered as the Onyx card (dark ink background, serif title,
 *      eyebrow + NO. ## header, footer with OPEN ↗).
 *   2. Embed view: one dashboard rendered full-bleed with a "back to all" button.
 *
 * Clicking a card transitions to embed view. The browser back button works
 * too (we use a state object, not router URL changes, but the UX feels right
 * for v1).
 *
 * Each card also has an "Open in Tableau" link in the detail view that opens
 * the dashboard in Tableau Cloud directly (new tab) — Cris's escape hatch when
 * he needs Tableau's full toolset.
 *
 * Folder field comes from the backend (apps/api/ashi_api/tenancy/client_config.py
 * reads it from clients/ashi/client.yaml). Empty folders are rendered under a
 * fallback "Other" section so any newly-added dashboard without a folder field
 * still shows up rather than vanishing silently.
 */
import { ArrowLeft } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { EmbedPane } from "../tableau/EmbedPane";
import {
  fetchTableauDashboards,
  type TableauDashboard,
  type TableauDashboardList,
} from "../lib/tableau";

// Folder display config. Order here is the display order on the page;
// labels are the human-readable section titles.
const FOLDERS: Array<{ key: string; label: string; eyebrow: string }> = [
  { key: "sales", label: "Sales", eyebrow: "Sales" },
  { key: "call_center", label: "Call Center", eyebrow: "Call Center" },
  { key: "marketing", label: "Marketing", eyebrow: "Marketing" },
  { key: "owner", label: "Owner", eyebrow: "Owner" },
];

// Fallback section for any dashboard whose folder field is empty or
// unrecognized. Keeps things visible during a misconfiguration rather than
// silently dropping cards.
const FALLBACK_FOLDER = { key: "", label: "Other", eyebrow: "Other" };

// Dashboards swapped from Tableau to a static HTML report. Key is the
// dashboard id from client.yaml; when present, the embed view renders the
// report in an iframe instead of a Tableau viz (no embed token involved).
// The entry stays in client.yaml so the card, folder grouping, and the
// per-user dashboard allowlist keep working unchanged.
const HTML_REPORTS: Record<string, string> = {
  // Company Sales Report (was Tableau workbook CompanySalesReport/SalesReport)
  "sales-report":
    "https://dafmyszu14d6o.cloudfront.net/company-sales-report.html",
  // Call Center Weekly Scorecard (was CallCenterWeeklyScorecard/WeeklyScorecard_1)
  "cc-weekly-scorecard":
    "https://dafmyszu14d6o.cloudfront.net/call-center-scorecard.html",
};

export function Dashboards() {
  const [data, setData] = useState<TableauDashboardList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openDashboard, setOpenDashboard] = useState<TableauDashboard | null>(null);

  useEffect(() => {
    fetchTableauDashboards()
      .then(setData)
      .catch((e) => setError(e.message));
  }, []);

  // Group dashboards by folder, preserving the declaration order from
  // client.yaml within each folder. The numbering inside a folder is just
  // the index within the folder's array (NO. 01, NO. 02, …).
  const groupedFolders = useMemo(() => {
    if (!data) return [];

    const known = new Set(FOLDERS.map((f) => f.key));
    const buckets = new Map<string, TableauDashboard[]>();

    for (const d of data.dashboards) {
      const folder = d.folder && known.has(d.folder) ? d.folder : FALLBACK_FOLDER.key;
      if (!buckets.has(folder)) buckets.set(folder, []);
      buckets.get(folder)!.push(d);
    }

    // Emit folders in the order defined above, then the fallback bucket if any.
    const result: Array<{ key: string; label: string; eyebrow: string; dashboards: TableauDashboard[] }> = [];
    for (const f of FOLDERS) {
      const items = buckets.get(f.key);
      if (items && items.length > 0) {
        result.push({ ...f, dashboards: items });
      }
    }
    const orphan = buckets.get(FALLBACK_FOLDER.key);
    if (orphan && orphan.length > 0) {
      result.push({ ...FALLBACK_FOLDER, dashboards: orphan });
    }
    return result;
  }, [data]);

  if (error) {
    return (
      <div className="p-8">
        <div
          className="rounded-md p-4 font-mono text-[13px]"
          style={{
            background: "var(--oxblood-bg)",
            color: "var(--oxblood)",
            border: "1px solid var(--oxblood)",
          }}
        >
          Failed to load dashboards: {error}
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div
        className="p-8 text-[14px] font-serif italic"
        style={{ color: "var(--ink-3)" }}
      >
        Loading dashboards…
      </div>
    );
  }

  // Embed view
  if (openDashboard) {
    return (
      <DashboardEmbedView
        dashboard={openDashboard}
        tableauServer={data.tableau_server}
        onBack={() => setOpenDashboard(null)}
      />
    );
  }

  // List view
  return (
    <div className="p-8 view-in overflow-y-auto h-full">
      <header className="mb-8">
        <div
          className="font-mono text-[10.5px] uppercase tracking-[0.16em] mb-2 font-semibold"
          style={{ color: "var(--ink-3)" }}
        >
          Workspace
        </div>
        <h1
          className="font-serif text-[28px] leading-tight"
          style={{ color: "var(--ink)" }}
        >
          Dashboards
        </h1>
        <p
          className="text-[14.5px] font-serif italic mt-1"
          style={{ color: "var(--ink-3)" }}
        >
          {data.dashboards.length} workbooks across Sales, Call Center, Marketing, and Owner views.
        </p>
      </header>

      {data.dashboards.length === 0 ? (
        <div
          className="rounded-md p-4 font-mono text-[13px]"
          style={{ background: "var(--bg-elev)", color: "var(--ink-3)" }}
        >
          No dashboards configured. Add entries to{" "}
          <code className="font-mono">tableau.dashboards</code> in{" "}
          <code className="font-mono">clients/ashi/client.yaml</code>.
        </div>
      ) : (
        <div className="space-y-12">
          {groupedFolders.map((folder) => (
            <section key={folder.key || "other"} aria-labelledby={`folder-${folder.key || "other"}`}>
              <div className="flex items-baseline justify-between mb-4">
                <h2
                  id={`folder-${folder.key || "other"}`}
                  className="font-serif text-[22px] tracking-tight uppercase"
                  style={{ color: "var(--ink)" }}
                >
                  {folder.label}
                </h2>
                <span
                  className="text-[10.5px] font-mono uppercase tracking-[0.14em]"
                  style={{ color: "var(--ink-4)" }}
                >
                  {folder.dashboards.length}{" "}
                  {folder.dashboards.length === 1 ? "workbook" : "workbooks"}
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {folder.dashboards.map((d, i) => (
                  <DashboardCard
                    key={d.id}
                    dashboard={d}
                    index={i}
                    eyebrow={folder.eyebrow}
                    onOpen={() => setOpenDashboard(d)}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * DashboardCard — the Onyx card design from the prototype.
 *
 * Dark ink background, bg-colored text and rules. Header rule under
 * eyebrow + NO. ##, then large serif uppercase title, description in the
 * body, footer rule with last-updated date (if available) and OPEN ↗.
 *
 * Entire card is a clickable button, no separate "Open" CTA — the OPEN ↗
 * in the corner doubles as the visual affordance.
 */
function DashboardCard({
  dashboard,
  index,
  eyebrow,
  onOpen,
}: {
  dashboard: TableauDashboard;
  index: number;
  eyebrow: string;
  onOpen: () => void;
}) {
  const num = `NO. ${String(index + 1).padStart(2, "0")}`;

  return (
    <button
      onClick={onOpen}
      className="group text-left flex flex-col t-base focus-ring overflow-hidden rounded"
      style={{
        background: "var(--ink)",
        color: "var(--bg)",
        border: "1px solid var(--ink)",
        minHeight: 248,
        padding: "18px 22px 16px",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-1px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "";
      }}
      aria-label={`Open ${dashboard.name}`}
    >
      {/* Header rule */}
      <div
        className="flex items-baseline justify-between gap-3 pb-2.5"
        style={{ borderBottom: "1px solid rgba(236,231,222,0.22)" }}
      >
        <span
          className="font-mono text-[10px] uppercase tracking-[0.16em] font-semibold truncate"
          style={{ color: "var(--bg)" }}
        >
          {eyebrow}
        </span>
        <span
          className="font-mono text-[10px] tracking-[0.10em] whitespace-nowrap"
          style={{ color: "var(--bg)" }}
        >
          {num}
        </span>
      </div>

      {/* Title */}
      <h3
        className="font-serif uppercase mt-4"
        style={{
          fontSize: 26,
          lineHeight: 1.04,
          letterSpacing: "-0.015em",
          color: "var(--bg)",
        }}
      >
        {dashboard.name}
      </h3>

      {/* Description */}
      {dashboard.description && (
        <p
          className="mt-2.5"
          style={{
            fontSize: 13,
            lineHeight: 1.45,
            fontWeight: 600,
            color: "var(--bg)",
            textWrap: "pretty",
          }}
        >
          {dashboard.description}
        </p>
      )}

      <div className="flex-1" />

      {/* Footer rule */}
      <div
        className="mt-3 pt-2.5 flex items-baseline justify-between"
        style={{ borderTop: "1px solid rgba(236,231,222,0.22)" }}
      >
        <span
          className="font-mono text-[10.5px] tracking-[0.02em]"
          style={{ color: "var(--bg)" }}
        >
          {dashboard.owner}
        </span>
        <span
          className="font-mono text-[10.5px] font-semibold tracking-[0.06em]"
          style={{ color: "var(--bg)" }}
        >
          OPEN ↗
        </span>
      </div>
    </button>
  );
}

function DashboardEmbedView({
  dashboard,
  tableauServer,
  onBack,
}: {
  dashboard: TableauDashboard;
  tableauServer: string;
  onBack: () => void;
}) {
  return (
    <div className="flex flex-col h-full view-in">
      {/* Top bar with back button */}
      <div
        className="px-6 py-3 border-b hairline flex items-center justify-between shrink-0"
        style={{ background: "var(--bg)" }}
      >
        <div className="flex items-center gap-3">
          <button
            onClick={onBack}
            className="text-[12px] font-mono uppercase tracking-[0.12em] px-3 py-1.5 rounded border hairline t-base hover:bg-[var(--hover)] focus-ring inline-flex items-center gap-1.5"
            style={{ color: "var(--ink-2)" }}
          >
            <ArrowLeft size={12} strokeWidth={1.5} />
            All dashboards
          </button>
          <div>
            <div
              className="font-serif text-[15px]"
              style={{ color: "var(--ink)" }}
            >
              {dashboard.name}
            </div>
            {dashboard.owner && (
              <div
                className="text-[10px] font-mono uppercase tracking-[0.14em]"
                style={{ color: "var(--brass)" }}
              >
                {dashboard.owner}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Embed pane fills the rest. Vertical scroll: a fixed-layout viz is
          laid out at authored-height * scale, which can exceed the pane —
          the pane scrolls it (Tableau no longer scrolls internally once the
          viz renders at its authored size). Horizontal stays clipped; the
          scale-to-fit in EmbedPane guarantees the width fits. An HTML
          report fills the pane and scrolls inside its own iframe instead. */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        {HTML_REPORTS[dashboard.id] ? (
          <iframe
            src={HTML_REPORTS[dashboard.id]}
            title={dashboard.name}
            className="w-full h-full"
            style={{ border: 0, display: "block", minHeight: 1200 }}
          />
        ) : (
          <EmbedPane
            dashboardId={dashboard.id}
            contentUrl={dashboard.content_url}
            tableauServer={tableauServer}
          />
        )}
      </div>
    </div>
  );
}
